// Pure geometry helpers for ISD block rendering.
// No google.maps dependency — unit-testable.

export const METERS_PER_FOOT = 0.3048;
export const FEET_PER_METER = 1 / METERS_PER_FOOT;
const EARTH_RADIUS_M = 6378137;

export const FT_INNER = 4;      // inner red disc
export const FT_MIDDLE = 6;     // middle blue ring
export const FT_OUTER = 14.5;   // outer yellow ring
export const DEFAULT_ARROW_LENGTH_FT = 20;
export const MIN_DRAG_FT = 2;   // below this, treat mouseup as a click

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

/**
 * Given a {lat,lng} origin, bearing in degrees (0=N, cw), and distance in feet,
 * return the destination {lat,lng} using spherical-earth math.
 */
export function computeDestinationFeet(origin, bearingDeg, distanceFeet) {
  const distM = distanceFeet * METERS_PER_FOOT;
  const angular = distM / EARTH_RADIUS_M;
  const brg = toRad(bearingDeg);
  const lat1 = toRad(origin.lat);
  const lng1 = toRad(origin.lng);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) +
    Math.cos(lat1) * Math.sin(angular) * Math.cos(brg),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(brg) * Math.sin(angular) * Math.cos(lat1),
    Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
  );
  return { lat: toDeg(lat2), lng: toDeg(lng2) };
}

/**
 * Initial bearing (degrees, 0=N, cw) from `a` to `b` in [0, 360).
 */
export function computeBearingDeg(a, b) {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  const deg = toDeg(Math.atan2(y, x));
  return (deg + 360) % 360;
}

/**
 * Great-circle distance between two {lat,lng} in feet.
 */
export function computeDistanceFeet(a, b) {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(h)));
  return EARTH_RADIUS_M * c * FEET_PER_METER;
}

/**
 * Convert azimuth (deg, 0=N cw) to the screen-rotation of an HTML label
 * whose baseline should follow the *tangent* of a circle at the point
 * where the azimuth vector hits the edge. Flips 180° when the text would
 * otherwise read upside-down.
 *
 * At azimuth=0 (label at top of circle) the tangent is horizontal → 0°.
 * At azimuth=90 (label at right of circle) the tangent is vertical → 90°.
 */
export function smartRotateDeg(azimuthDeg) {
  let r = ((azimuthDeg % 360) + 360) % 360;
  if (r > 90 && r < 270) r = (r + 180) % 360;
  return r;
}
